/**
 * Inventories API Types
 *
 * Type definitions for Inventory data from Rails backend.
 */

export interface InventoryImage {
  signed_id: string;
  url: string;
  filename: string;
}

// Mirrors the InventorySerializer's `serialized_inventory_configurations` output.
// Canonical home for the shape; UI types re-export this.
export interface InventoryConfiguration {
  id: number;
  quantity: number | null;
  value: number | null;
  default_type: string | null;
  default_event_type: string | null;
  multiplier: string | null;
}

export interface InventoryApiItem {
  id: number;
  name: string;
  active: boolean;
  category: string | null;
  location: string | null;
  recommended_retail_price: number | null;
  cost_to_company: number | null;
  quantity: number | null;
  container_unit: number | null;
  images: InventoryImage[];
  inventory_configurations?: InventoryConfiguration[];
  created_at: string;
  updated_at: string;
}

export interface InventoriesResponse {
  inventories: InventoryApiItem[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    sort_by: string;
    sort_direction: string;
  };
  total_unfiltered: number;
}

export type InventoryApiStatus = 'active' | 'inactive';

// Source: app/models/inventory.rb - Inventory::Categories
export const INVENTORY_CATEGORIES = {
  FOOD_CONTAINER: 'Food container',
  SETUP_EQUIPMENT: 'Setup equipment',
  GENERAL_EQUIPMENT: 'General equipment',
  CONSUMABLES: 'Consumables',
  CANAPE_EQUIPMENT: 'Canape equipment',
  CHAFING_DISH_EQUIPMENT: 'Chafing dish equipment',
  BAMBOO_STEAMER_EQUIPMENT: 'Bamboo steamer equipment',
  SOUP_POT_EQUIPMENT: 'Soup pot equipment',
  DRINKS_EQUIPMENT: 'Drinks equipment',
  SERVING_EQUIPMENT: 'Serving utensils',
} as const;

export interface FetchInventoriesParams {
  status?: InventoryApiStatus;
  category?: string;
  q?: string;
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface InventoryShowResponse {
  inventory: InventoryApiItem;
}

export interface InventoryConfigurationAttributes {
  id?: number;
  quantity?: number | null;
  value?: number | null;
  default_type?: string | null;
  default_event_type?: string | null;
  multiplier?: string | null;
  _destroy?: boolean;
}

export interface UpdateInventoryPayload {
  name?: string;
  category?: string | null;
  location?: string | null;
  recommended_retail_price?: number | null;
  cost_to_company?: number | null;
  active?: boolean;
  inventory_configurations_attributes?: InventoryConfigurationAttributes[];
  images?: File[];
  remove_image_ids?: string[];
}

export interface CreateInventoryPayload {
  name: string;
  category: string;
  location: string;
  recommended_retail_price?: number | null;
  cost_to_company?: number | null;
  images?: File[];
}
